"use strict";
/**
 * Direct gap solver.
 *
 * We express the projected retirement corpus as:
 *   projected =
 *       existingLumpSum_FV               // FV of (existing + lumpNow) at retirement
 *     + currentSip_FV                    // FV of current SIP schedule
 *     + expectedLumpSumAtRetirement      // added at retirement, no growth
 *
 * The "required" side needs
 *   required = required TOTAL retirement corpus
 *            = requiredWithdrawalCorpus
 *              + healthcareReserve
 *              + oneTimeExpense
 *
 * If required > projected, we can close the gap by increasing SIP.
 * A ₹1 STARTING monthly SIP grows to `unitStepUpSipFutureValueFactor`
 * at retirement (step-ups compound annually). So the ADDITIONAL
 * starting monthly SIP required is:
 *
 *   additionalMonthlySipRequired = gap / unitStepUpSipFutureValueFactor
 *
 * Similarly, the ADDITIONAL lump sum today required is:
 *
 *   additionalLumpSumRequiredToday = gap / (1 + monthlyRate) ^ months
 *
 * These are direct solves — no iteration, no false-position.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.solveGap = void 0;
const accumulation_1 = require("./accumulation");
const rates_1 = require("./rates");
function solveGap(inputs, required) {
    const years = Math.max(0, inputs.retirementAge - inputs.currentAge);
    const months = years * 12;
    const monthlyRate = (0, rates_1.effectiveAnnualToMonthly)(inputs.accumulationReturn);
    const alreadyRetired = years === 0;
    const accum = (0, accumulation_1.projectAccumulation)(inputs);
    const projected = accum.projectedRetirementCorpus;
    const unitFactor = (0, accumulation_1.unitStepUpSipFutureValueFactor)(inputs);
    // Decompose the current-SIP contribution.
    const growthFactor = (0, rates_1.monthlyGrowthFactor)(monthlyRate, months);
    const existingLumpSumFV = ((inputs.existingRetirementInvestments || 0) +
        (inputs.lumpSumAvailableNow || 0)) *
        growthFactor;
    const currentSipFV = (inputs.currentMonthlySip || 0) * unitFactor;
    const gap = required.requiredTotalRetirementCorpus - projected;
    let additionalMonthlySipRequired = 0;
    let additionalLumpSumRequiredToday = 0;
    if (!alreadyRetired && gap > 0) {
        if (unitFactor > 0) {
            additionalMonthlySipRequired = gap / unitFactor;
        }
        if (growthFactor > 0) {
            additionalLumpSumRequiredToday = gap / growthFactor;
        }
    }
    const requiredStartingMonthlySip = (inputs.currentMonthlySip || 0) + additionalMonthlySipRequired;
    return {
        gap,
        requiredStartingMonthlySip,
        additionalMonthlySipRequired,
        additionalLumpSumRequiredToday,
        futureValueOfExistingAndImmediateLumpSum: existingLumpSumFV,
        futureValueOfCurrentSip: currentSipFV,
        unitStepUpSipFutureValueFactor: unitFactor,
        accumulationYears: years,
        alreadyRetired,
    };
}
exports.solveGap = solveGap;
